import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import { LegalH2 as H2, LegalP as P, LegalDivider as Divider } from './_legalHelpers';

const MEDICAL_VERSION = 'BMJ-MEDICAL-DECLARATION-V1.0-2026-05';
const CONSENT_VERSION = 'BMJ-VACCINE-CONSENT-V1.0-2026-05';

export default function MedicalVaccinationConsent() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const effectiveDate = new Date().toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div data-testid="medical-vaccination-consent-page" className="bg-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link to="/" data-testid="consent-breadcrumb"
            className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.2em] text-[#673c96]/70 hover:text-[#f37721] transition-colors mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}>
            ← Back to Home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#311b52] mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}>
            Medical & Vaccination Consent
          </h1>

          <div className="flex flex-col gap-1 mb-10 pb-10 border-b border-[#673c96]/10">
            <p className="text-[13px] text-[#673c96]/80" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span className="font-semibold">Effective Date:</span> {effectiveDate}
            </p>
            <p className="text-[13px] text-[#673c96]/80" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span className="font-semibold">Medical Declaration version:</span> {MEDICAL_VERSION}
            </p>
            <p className="text-[13px] text-[#673c96]/80" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span className="font-semibold">Vaccination Consent version:</span> {CONSENT_VERSION}
            </p>
          </div>

          <P>
            This page reproduces the Medical Declaration and Vaccination Consent that you confirm during a BookMyJab booking. By placing a booking, you confirm that you understand and accept the statements below for yourself, or on behalf of your dependent / minor as their parent or legal guardian.
          </P>

          <H2>Medical Declaration</H2>
          <P>
            I confirm that all information provided by me for this booking is true, complete and not misleading, including age, allergies, prior vaccine reactions, current illness, fever, pregnancy status, immune status, chronic medical conditions, ongoing medication, blood thinner usage, history of fainting/anaphylaxis, and any doctor&rsquo;s advice received.
          </P>
          <P>
            I understand that incorrect, incomplete or concealed medical information may increase the risk of adverse reaction and may affect the suitability of vaccination.
          </P>
          <P>
            I understand that BookMyJab, its doctors, nurses, employees and partners shall not be responsible for consequences arising from false, incomplete, inaccurate or withheld information provided by me.
          </P>

          <Divider />

          <H2>Vaccination Consent</H2>
          <P>
            I understand that vaccination is a medical procedure involving administration of a biological product into the body.
          </P>
          <P>
            I understand the nature, purpose, expected benefits, limitations, alternatives and possible risks of the selected vaccine for myself or my dependent. I have had the opportunity to ask questions or seek doctor clarification before administration.
          </P>
          <P>
            I understand that common side effects may include pain, redness, swelling, fever, fatigue, headache, body ache, dizziness, nausea or mild allergic reaction.
          </P>
          <P>
            I understand that rare but serious adverse events may include severe allergy, anaphylaxis, fainting, seizure, hospitalisation, disability or death.
          </P>
          <P>
            I understand that no vaccine guarantees 100% protection, immunity, absence of infection, absence of side effects or absence of adverse events.
          </P>
          <P>
            I voluntarily consent to administration of the selected vaccine only. Consent for one vaccine shall not be treated as consent for any other vaccine, procedure, medicine or treatment.
          </P>

          <Divider />

          <H2>Guardian Declaration (Minor Recipient)</H2>
          <P>
            When the booking is for a minor (under 18), the parent or legal guardian provides verifiable consent on the child&rsquo;s behalf:
          </P>
          <P className="italic">
            &ldquo;I confirm that I am the parent/legal guardian or authorised representative of the child and I provide consent for this vaccination booking, administration and processing of the child&rsquo;s personal and health data.&rdquo;
          </P>
          <P>
            For bookings using a customer-supplied vaccine, the following additional guardian declaration applies:
          </P>
          <P className="italic">
            &ldquo;I confirm I am the parent or legal guardian of the minor recipient and I provide consent for the administration of this customer-supplied vaccine on their behalf.&rdquo;
          </P>

          <Divider />

          <H2>Related Documents</H2>
          <P>
            See also: <Link to="/terms-of-service" className="text-[#f37721] hover:underline font-medium">Terms of Service</Link> · <Link to="/privacy-policy" className="text-[#f37721] hover:underline font-medium">Privacy Policy</Link> · <Link to="/refund-policy" className="text-[#f37721] hover:underline font-medium">Refund Policy</Link> · <Link to="/grievance" className="text-[#f37721] hover:underline font-medium">Grievance & Data Rights</Link>
          </P>

          <H2>Contact</H2>
          <P>
            For questions about this consent, please contact <a href="mailto:bookmyjab@gmail.com" className="text-[#f37721] hover:underline font-medium" data-testid="consent-support-email">bookmyjab@gmail.com</a> or call <a href="tel:+919873554343" className="text-[#f37721] hover:underline font-medium">+91 9873 55 4343</a>.
          </P>
        </div>
      </main>
      <Footer />
    </div>
  );
}
